// v50.309 — MobileBottomNav (4 tabs + centred blue FAB).
//
// Restores the v48.x "raised centre FAB" pattern Sam asked to bring
// back. The bar is 64px tall with five cells: four flat nav tabs and
// a 60px raised blue FAB in the centre.
//
// Tabs (left → right):
//   1. Today's checklist  — deep-links to /projects/site-ops where
//                            the 5-checkpoint daily band lives.
//   2. Find labour (coord) / Find work (partner) — role-aware
//                            discovery entry point.
//   3. [+ FAB]            — opens the full Apple-style side nav with
//                            expandable dropdowns (MobileNavSheet).
//                            This is the "fully nav the platform"
//                            affordance, not the quick-actions sheet.
//   4. Quick actions      — opens MobileFabSheet (the existing role-
//                            aware "what do I want to do" sheet).
//   5. Inbox              — messages + invitations + responses.
//
// Mobile-only. Desktop renders Sidebar + Topbar unchanged; this file
// is never mounted at sm+ breakpoints.

import { useLocation, useNavigate } from "react-router";
import { useShellUser } from "@/features/auth/AuthContext";
import { useEffectiveRole } from "../roleOverride";
import { useSidebarBadges } from "../useSidebarBadges";

type IconKey = "checklist" | "search" | "briefcase" | "fab" | "zap" | "inbox";

interface Tab {
  id:               "today-check" | "find" | "fab" | "quick" | "mail";
  /* Destination path. null for FAB + Quick which open sheets. */
  path:             string | null;
  label:            string;
  iconKey:          IconKey;
  /* When true, render the raised centre FAB chrome instead of the
     flat tab style. Only the centre cell ever sets this. */
  isFab?:           boolean;
  /* Trigger handler — fires onOpenMore (FAB → full nav sheet) or
     onOpenFab (quick → quick-actions sheet). null = navigate. */
  trigger?:         "more" | "fab";
  showInboxBadge?:  boolean;
}

/* v50.310 — Today tab rewired from /projects/site-ops to /dashboard
   per Sam's request. The dashboard already surfaces the daily Site
   Ops checklist (SiteOpsWorkflowBand) at the top of the "Needs you
   now" band, so the user still lands on today's checklist content —
   just inside the broader dashboard context where the chase banner,
   pinned sign-off, and Actions Required tiles all live too. */
const COORD_TABS: Tab[] = [
  { id: "today-check", path: "/dashboard",   label: "Today",       iconKey: "checklist" },
  { id: "find",        path: "/find-labour", label: "Find labour", iconKey: "search" },
  { id: "fab",         path: null,           label: "Menu",        iconKey: "fab", isFab: true, trigger: "more" },
  { id: "quick",       path: null,           label: "Quick",       iconKey: "zap",   trigger: "fab" },
  { id: "mail",        path: "/inbox",       label: "Inbox",       iconKey: "inbox", showInboxBadge: true },
];

const PARTNER_TABS: Tab[] = [
  { id: "today-check", path: "/dashboard",   label: "Today",       iconKey: "checklist" },
  { id: "find",        path: "/find-work",   label: "Find work",   iconKey: "briefcase" },
  { id: "fab",         path: null,           label: "Menu",        iconKey: "fab", isFab: true, trigger: "more" },
  { id: "quick",       path: null,           label: "Quick",       iconKey: "zap",   trigger: "fab" },
  { id: "mail",        path: "/inbox",       label: "Inbox",       iconKey: "inbox", showInboxBadge: true },
];

interface Props {
  onOpenFab:  () => void;
  onOpenMore: () => void;
}

export function MobileBottomNav({ onOpenFab, onOpenMore }: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user     = useShellUser();
  const role     = useEffectiveRole();
  const badges   = useSidebarBadges();
  const inbox    = badges?.inbox || 0;

  if (!user) return null;
  const tabs = role === "partner" ? PARTNER_TABS : COORD_TABS;

  const isActive = (t: Tab): boolean => {
    if (!t.path) return false;
    /* Today tab → /dashboard or /today (the redirect alias) or /. */
    if (t.id === "today-check") {
      return pathname === "/dashboard" || pathname === "/today" || pathname === "/";
    }
    /* Find tab: highlight on its area's legacy roots too. */
    if (t.id === "find") {
      if (role === "partner") return pathname.startsWith("/find-work") || pathname.startsWith("/requirements") || pathname.startsWith("/tenders");
      return pathname.startsWith("/find-labour") || pathname.startsWith("/partners");
    }
    if (t.id === "mail") return pathname.startsWith("/inbox");
    return pathname === t.path;
  };

  return (
    <nav
      aria-label="Mobile navigation"
      className="lc-safe-pb fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-lc-line flex items-stretch"
      style={{
        height: 64,
        WebkitBackdropFilter: "saturate(180%) blur(14px)",
        boxShadow: "0 -6px 18px rgba(15,23,42,.06)",
      }}
    >
      {tabs.map(t => {
        const onClick = t.trigger === "more"
          ? onOpenMore
          : t.trigger === "fab"
          ? onOpenFab
          : () => t.path && navigate(t.path);
        const active = isActive(t);
        const showBadge = t.showInboxBadge && inbox > 0;

        /* Raised centre FAB — lifts above the baseline, brand blue
           gradient, 56px round, white plus icon. */
        if (t.isFab) {
          return (
            <button
              key={t.id}
              type="button"
              onClick={onClick}
              aria-label="Open full menu"
              className="flex-1 relative flex items-start justify-center pt-1"
              style={{
                background: "transparent",
                border: "none",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <span
                aria-hidden="true"
                className="absolute -top-5 left-1/2 -translate-x-1/2 w-[60px] h-[60px] rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, var(--color-lc-blue), var(--color-lc-blue-dark))",
                  boxShadow: "0 8px 18px rgba(37,99,235,0.35), 0 2px 4px rgba(15,23,42,0.12)",
                  border: "3px solid #fff",
                }}
              >
                <TabIcon name="fab" size={24} active />
              </span>
              <span
                className="absolute bottom-2.5 left-0 right-0 text-center"
                style={{ fontSize: 10, fontWeight: 700, color: "#475569", letterSpacing: ".01em" }}
              >
                Menu
              </span>
            </button>
          );
        }

        return (
          <button
            key={t.id}
            type="button"
            onClick={onClick}
            aria-current={active ? "page" : undefined}
            aria-label={t.label}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[44px] pt-1.5 relative"
            style={{
              color: active ? "var(--color-lc-blue-dark)" : "#475569",
              fontSize: 10,
              fontWeight: active ? 900 : 700,
              letterSpacing: active ? "-0.005em" : ".01em",
              WebkitTapHighlightColor: "transparent",
              background: "transparent",
              border: "none",
            }}
          >
            {active && (
              <span
                aria-hidden="true"
                className="absolute top-0 left-[36%] right-[36%] h-[3px] rounded-sm"
                style={{ background: "linear-gradient(135deg, var(--color-lc-blue), var(--color-lc-blue-dark))" }}
              />
            )}
            <span
              aria-hidden="true"
              className="w-10 h-[30px] rounded-full flex items-center justify-center relative transition-colors"
              style={{ background: active ? "var(--color-lc-blue-light)" : "transparent" }}
            >
              <TabIcon name={t.iconKey} size={22} active={active} />
              {showBadge && (
                <span
                  aria-label={`${inbox} unread`}
                  className="absolute -top-0.5 right-1 min-w-[16px] h-4 px-1 rounded-full bg-lc-red text-white border-2 border-white text-[9px] font-extrabold flex items-center justify-center leading-none"
                >
                  {inbox > 99 ? "99+" : inbox}
                </span>
              )}
            </span>
            <span style={{ lineHeight: 1.1 }}>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function TabIcon({
  name, size, active,
}: {
  name: IconKey;
  size: number;
  active?: boolean;
}) {
  const color = name === "fab" ? "#FFFFFF" : active ? "var(--color-lc-blue-dark)" : "var(--color-lc-mute)";
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 2.2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
  switch (name) {
    case "checklist":
      return (
        <svg {...common}>
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      );
    case "search":
      return <svg {...common}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>;
    case "briefcase":
      return (
        <svg {...common}>
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      );
    case "fab":
      return <svg {...common} strokeWidth={2.6}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
    case "zap":
      return <svg {...common}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
    case "inbox":
      return <svg {...common}><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>;
  }
}
